/**
 * @file SectionGrid.tsx
 * @description Сетка секции на вкладке «Содержимое» (phase19.16 Р2;
 * uiux-design-r2 §2, §4, §7, §10): колонки одна под другой (панель узкая),
 * в колонке — строки элементов и «⊕ элемент». Элементы перетаскиваются
 * внутри секции, в том числе между колонками (dnd-kit; пустая колонка —
 * место для броска). Замена прежнего `SectionGridEditor` (секция v1).
 */

import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useDroppable, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { DND_ACCESSIBILITY_RU } from "../../../../lib/site-builder/dnd-ru";
import type { SectionElementKind, SectionPayload } from "@chaiberi/shared";
import type { ReactNode } from "react";

import { AddElementMenu, COLUMN_FULL_TEXT } from "./AddElementMenu";
import { useNotify } from "../../../ui/NotifyProvider";
import { ElementRow } from "./ElementRow";
import { useSectionForm } from "../section-form-context";
import {
  columnFull,
  duplicateElement,
  findElement,
  insertElement,
  moveElementBy,
  moveElementTo,
  removeElement,
} from "../../../../lib/site-builder/section-edit";

interface Props {
  payload: SectionPayload;
  onChange: (payload: SectionPayload) => void;
  selectedId: string | null;
  onSelect: (elementId: string | null, fresh?: boolean) => void;
}

const COLUMN_ID = (col: number) => `column:${col}`;

function ColumnDrop({ col, children }: { col: number; children: ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: COLUMN_ID(col) });
  return (
    <div ref={setNodeRef} className={isOver ? "rounded-lg bg-primary/5" : undefined}>
      {children}
    </div>
  );
}

export function SectionGrid({ payload, onChange, selectedId, onSelect }: Props) {
  const form = useSectionForm();
  const notify = useNotify();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const columns = payload.columns.items;

  function onDragEnd(event: DragEndEvent) {
    const id = String(event.active.id);
    const overId = event.over ? String(event.over.id) : null;
    if (!overId || overId === id) return;
    const from = findElement(payload, id);
    const col = overId.startsWith("column:") ? Number(overId.slice(7)) : (findElement(payload, overId)?.col ?? -1);
    if (!from || col < 0) return;
    // В заполненную колонку из другой — нельзя (SB64): объясняем, а не молчим.
    if (from.col !== col && columnFull(payload, col)) {
      notify.error(COLUMN_FULL_TEXT);
      return;
    }
    const row = overId.startsWith("column:") ? (columns[col]?.elements.length ?? 0) : (findElement(payload, overId)?.row ?? 0);
    onChange(moveElementTo(payload, id, col, row));
  }

  function add(col: number, kind: SectionElementKind) {
    const next = insertElement(payload, col, kind);
    if (!next) return;
    onChange(next.payload);
    onSelect(next.id, true);
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd} accessibility={DND_ACCESSIBILITY_RU}>
      <div className="flex flex-col gap-3">
        {columns.map((column, col) => (
          <ColumnDrop key={col} col={col}>
            <section aria-label={`Колонка ${col + 1}`} className="flex flex-col gap-1 rounded-xl p-2 [box-shadow:inset_0_0_0_1px_var(--outline-variant)]">
              {columns.length > 1 ? <span className="type-caption px-1 text-on-surface-variant">Колонка {col + 1}</span> : null}
              <SortableContext items={column.elements.map((el) => el.id)} strategy={verticalListSortingStrategy}>
                <ul className="flex flex-col gap-1">
                  {column.elements.map((element, row) => (
                    <ElementRow
                      key={element.id}
                      element={element}
                      selected={element.id === selectedId}
                      first={row === 0}
                      last={row === column.elements.length - 1}
                      invalid={form.hasIssuesUnder(`columns.items.${col}.elements.${row}`)}
                      onSelect={() => onSelect(element.id)}
                      actions={{
                        duplicate: () => {
                          const next = duplicateElement(payload, element.id);
                          if (next) onChange(next.payload);
                          else notify.error(COLUMN_FULL_TEXT);
                        },
                        up: () => onChange(moveElementBy(payload, element.id, -1)),
                        down: () => onChange(moveElementBy(payload, element.id, 1)),
                        remove: () => {
                          onChange(removeElement(payload, element.id));
                          if (selectedId === element.id) onSelect(null);
                        },
                      }}
                    />
                  ))}
                </ul>
              </SortableContext>
              {column.elements.length === 0 ? <p className="type-caption px-1 py-2 text-on-surface-variant">Пустая колонка — добавьте элемент.</p> : null}
              <AddElementMenu full={columnFull(payload, col)} onAdd={(kind) => add(col, kind)} />
            </section>
          </ColumnDrop>
        ))}
      </div>
    </DndContext>
  );
}
